import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { citationLiteratureFormatter, patentFamilyFormatter } from '@Services/FormatUtils';
import CitationsButtonsComponent from '@Components/Results/reactions/reaction-details/citations-buttons.component';
import ListOfAuthorsComponent from '@Components/Results/reactions/reaction-details/list-of-authors.component';
import DocumentJournalsComponent from '@Components/Results/documents/document-journals.component';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';

export const NO_AUTHOR_LABEL = 'No author';
export const AUTHOR_PENDING_LABEL = 'Author information pending';
/**
 * Get no author document label depending on publication year
 * @param publicationYear - may contain html '<hi>2020</hi>'
 * @returns {string} label
 */
export function getDocumentNoAuthorLabel(publicationYear) {
  if (!publicationYear) {
    return NO_AUTHOR_LABEL;
  }
  return publicationYear.toString().indexOf(new Date().getFullYear().toString()) >= 0 ? AUTHOR_PENDING_LABEL : NO_AUTHOR_LABEL;
}

export default class ReactionCitationsComponent extends React.PureComponent {
  static literatureFormatter(content) {
    return citationLiteratureFormatter(content);
  }

  static patentFormatter(content) {
    return patentFamilyFormatter(content);
  }

  constructor(props) {
    super(props);

    this.state = {
      isAbstractExpanded: false,
    };
  }

  // eslint-disable-next-line react/sort-comp
  toggleAbstractExpanded(val) {
    this.setState((prevState) => ({ isAbstractExpanded: !isUndefined(val) ? val : !prevState.isAbstractExpanded }));
  }

  getDefaultCitationTemplate() {
    const { metadata, citation, onAuthorClick } = this.props;
    const author = !isEmpty(citation.citationJournals) ? getDocumentNoAuthorLabel(citation.year) : '';

    return (
      <>
        {citation.authorKeywords && (
          <div className="content-literature">
            <span className="rx-element-literature">{citation.authorKeywords.join(', ')}</span>
          </div>
        )}
        <div className="content-authors">
          <span className="rx-element-authors">
            {citation.authors ? (
              <ListOfAuthorsComponent key={citation.id} metadata={metadata} citation={citation} onAuthorClick={onAuthorClick} />
            ) : (
              author
            )}
          </span>
          {!isEmpty(citation.citationJournals) && <DocumentJournalsComponent journals={citation.citationJournals} />}
        </div>
      </>
    );
  }

  getPatentTemplate() {
    const { citation } = this.props;
    const citationPatentsHtml = ` - ${this.constructor.literatureFormatter(citation.citationPatents)}`;

    return [
      <div key="0" className="content-authors">
        <span className="rx-element-assignee">
          {citation.assignees ? (
            <>
              {!Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) && <span>Current Patent Assignee: </span>}
              <InnerHTML tag="span" html={citation.assignees} />
            </>
          ) : (
            getDocumentNoAuthorLabel(citation.year)
          )}
        </span>
        <InnerHTML tag="span" className="rx-element-literature" html={citationPatentsHtml} />
        {!isEmpty(citation.citationJournals) && <DocumentJournalsComponent journals={citation.citationJournals} />}
      </div>,
      <div key="1" className="content-literature">
        {citation.patentFamily && (
          <span className="rx-element-literature">
            <span className="rx-element-literature-title">Patent Family Members:</span>
            <InnerHTML tag="span" html={this.constructor.patentFormatter(citation.patentFamily)} />
          </span>
        )}
      </div>,
      citation.location ? (
        <div key="2" className="content-location-in-patent">
          Location in patent:&nbsp;
          <InnerHTML tag="span" html={citation.location} />
        </div>
      ) : null,
    ];
  }

  getAbstractTemplate() {
    const { citation, index } = this.props;

    return (
      <div className={`result-detail-panel reaction-citations-abstract-${index}`}>
        <div className="result-detail-panel-wrapper">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <i className="sub-menu-close ss-delete" onClick={() => this.toggleAbstractExpanded(false)} />
          <h4>Abstract</h4>
          <InnerHTML tag="p" className="result-detail-block typography-small" html={citation.abstractText} />
        </div>
      </div>
    );
  }

  render() {
    const { metadata, citation, index, onFullTextLinkClick, onIssueCitedCounterClick, onAbstractOpen } = this.props;
    const { isAbstractExpanded } = this.state;
    const isPatentType = citation.type && citation.type.indexOf('Patent') !== -1;
    return (
      <div className="citation clear">
        {/* default citation template */}
        {!isPatentType && !citation.abstractText && this.getDefaultCitationTemplate()}
        {/* Patent */}
        {isPatentType && this.getPatentTemplate()}
        {/* Citations buttons */}
        <CitationsButtonsComponent
          metadata={metadata}
          citation={citation}
          index={index}
          isAbstractExpanded={isAbstractExpanded}
          toggleAbstractExpanded={this.toggleAbstractExpanded}
          onFullTextLinkClick={onFullTextLinkClick}
          onIssueCitedCounterClick={onIssueCitedCounterClick}
          onAbstractOpen={onAbstractOpen}
        />

        {/* Abstract */}
        {citation.abstractText && isAbstractExpanded && this.getAbstractTemplate()}
      </div>
    );
  }
}

ReactionCitationsComponent.defaultProps = {
  onFullTextLinkClick: () => null,
  onIssueCitedCounterClick: () => null,
  onAbstractOpen: () => null,
  onAuthorClick: () => null,
};

ReactionCitationsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  index: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  citation: PropTypes.object.isRequired,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
};
